import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Avatar,
    Box,
    ButtonBase,
    Typography,
    useMediaQuery,
} from "@mui/material";
import { HowToVote, LockOutlined } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import TrapFocus from "@mui/material/Unstable_TrapFocus";
import { motion } from "framer-motion";
import BackdropOverlay from "../common/BackdropOverlay.jsx";
import { getMapMetaById, getMapImageUrl } from "../../constants/mapMeta";
import {
    MAP_VOTE_DEFAULT_DURATION_SECONDS,
    MAP_VOTE_HANDOFF_MS,
} from "../../constants/mapVote";
import utils from "../../universal/utils";
import { colors } from "../../theme/colors";
import {
    MOBILE_NAV_HEIGHT,
    MOBILE_NAV_MEDIA,
} from "../layout/layoutConstants";

const MotionBox = motion(Box);

// Grace after the deadline before the modal gives up waiting for the server.
// This is the only client-side escape from a blocking overlay, so it must fire
// even when no resolve or redirect ever arrives.
const RESOLVE_GRACE_MS = 20000;
// How long the winning map stays on screen before the modal releases the UI.
const WINNER_DISPLAY_MS = 5000;

function remainingSeconds(deadline, durationSeconds) {
    // The server deadline is authoritative; counting down from durationSeconds would drift across clients.
    if (deadline != null && Number.isFinite(Number(deadline))) {
        return Math.max(0, Math.ceil((Number(deadline) - Date.now()) / 1000));
    }
    const fallback = Number(durationSeconds);
    return Number.isFinite(fallback) ? Math.max(0, Math.ceil(fallback)) : 0;
}

function voterInitial(nickname) {
    const trimmed = String(nickname || "").trim();
    return trimmed ? trimmed.charAt(0).toUpperCase() : "?";
}

function waitingLabel(pendingCount, rosterCount) {
    if (rosterCount === 0) return "Waiting for players";
    if (pendingCount <= 0) return "All players have picked";
    if (pendingCount === 1) return "1 player hasn't picked";
    return `${pendingCount} players haven't picked`;
}

function resolvedVoteLabel({ winnerId, winnerMeta, wasTie, tieBrokenBy, topCount }) {
    const name = winnerMeta?.display || winnerId;
    if (!winnerId) return "";

    const count = Number(topCount);
    const scorePrefix =
        wasTie && Number.isFinite(count) && count > 0
            ? `Tied ${count}–${count} — `
            : wasTie
              ? "Tied — "
              : "";

    if (wasTie && tieBrokenBy === "pre_selected") {
        return `${scorePrefix}${name} wins, the map you accepted`;
    }
    if (wasTie && tieBrokenBy === "seeded_random") {
        return `${scorePrefix}${name} won the coin flip`;
    }
    return `${name} won the vote`;
}

function formatTeamRecord(record) {
    const wins = Number(record?.wins) || 0;
    const losses = Number(record?.losses) || 0;
    const gamesPlayed = Number(record?.gamesPlayed) || wins + losses;
    if (gamesPlayed <= 0) return null;
    const winRate = Number.isFinite(Number(record?.winRate))
        ? Math.round(Number(record.winRate))
        : Math.round((wins / gamesPlayed) * 100);
    return `${wins}W-${losses}L (${winRate}%)`;
}

function MapTeamRecords({ mapId, teamStats }) {
    const windowDays =
        Number(teamStats?.windowDays) > 0 ? Number(teamStats.windowDays) : 90;
    const row = teamStats?.maps?.[mapId];
    const yours = formatTeamRecord(row?.yourTeam);
    const theirs = formatTeamRecord(row?.opponentTeam);
    const emptyLabel = `No ${windowDays}d data`;

    return (
        <Box
            sx={{
                mt: "0.35rem",
                display: "flex",
                flexDirection: "column",
                gap: "0.08rem",
                minWidth: 0,
            }}
        >
            <Typography
                sx={{
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    lineHeight: 1.25,
                    color: colors.text.primary,
                    fontVariantNumeric: "tabular-nums",
                }}
            >
                Your team {yours || emptyLabel}
            </Typography>
            <Typography
                sx={{
                    fontSize: "0.68rem",
                    fontWeight: 600,
                    lineHeight: 1.25,
                    color: colors.text.secondary,
                    fontVariantNumeric: "tabular-nums",
                }}
            >
                Other team {theirs || emptyLabel}
            </Typography>
        </Box>
    );
}

function cooldownLabel(blocked) {
    const meta = getMapMetaById(blocked?.mapId);
    const name = meta?.display || blocked?.mapId;
    const streak = Number(blocked?.streak) || 0;
    const fixated = Number(blocked?.fixatedPlayers) || 0;
    const rosterSize = Number(blocked?.rosterSize) || 0;

    if (!rosterSize || !streak) return name;
    return `${name} — ${fixated} of ${rosterSize} players have played it ${streak} games in a row`;
}

// Without this the withheld map just silently disappears from the pool, which
// reads as a bug rather than a rule.
function MapCooldownNotice({ blockedMaps }) {
    if (!Array.isArray(blockedMaps) || blockedMaps.length === 0) return null;

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: "0.5rem",
                mb: { xs: "0.95rem", md: "1.05rem" },
                px: "0.75rem",
                py: "0.55rem",
                borderRadius: { xs: "12px", md: "14px" },
                border: `1px solid ${colors.border.default}`,
                backgroundColor: "rgba(255,255,255,0.03)",
            }}
        >
            <LockOutlined
                sx={{
                    fontSize: "0.95rem",
                    color: colors.text.muted,
                    mt: "0.1rem",
                    flexShrink: 0,
                }}
            />
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    sx={{
                        fontSize: "0.62rem",
                        fontWeight: 700,
                        letterSpacing: "0.14em",
                        textTransform: "uppercase",
                        color: colors.text.muted,
                    }}
                >
                    Resting this match
                </Typography>
                {blockedMaps.map((blocked) => (
                    <Typography
                        key={blocked?.mapId}
                        sx={{
                            fontSize: "0.72rem",
                            fontWeight: 600,
                            lineHeight: 1.45,
                            color: colors.text.secondary,
                        }}
                    >
                        {cooldownLabel(blocked)}
                    </Typography>
                ))}
            </Box>
        </Box>
    );
}

function MapVoterChip({ voter, isSelf }) {
    const nickname = voter?.nickname || "Player";
    const src = voter?.avatarUrl || undefined;

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.32rem",
                maxWidth: "100%",
                minWidth: 0,
                px: "0.28rem",
                py: "0.12rem",
                borderRadius: "999px",
                border: isSelf
                    ? `1px solid ${alpha(colors.primary.main, 0.42)}`
                    : `1px solid ${colors.border.subtle}`,
                backgroundColor: isSelf
                    ? colors.accent.subtle
                    : "rgba(255,255,255,0.03)",
            }}
        >
            <Avatar
                alt={nickname}
                src={src}
                sx={{
                    width: 18,
                    height: 18,
                    fontSize: "0.58rem",
                    fontWeight: 700,
                    flexShrink: 0,
                    backgroundColor: alpha(colors.primary.main, 0.18),
                    color: colors.primary.main,
                    border: `1px solid ${alpha(colors.primary.main, 0.22)}`,
                }}
            >
                {src ? null : voterInitial(nickname)}
            </Avatar>
            <Typography
                noWrap
                title={nickname}
                sx={{
                    fontSize: "0.68rem",
                    fontWeight: isSelf ? 700 : 600,
                    color: isSelf ? colors.text.primary : colors.text.secondary,
                    lineHeight: 1.2,
                    minWidth: 0,
                }}
            >
                {nickname}
            </Typography>
        </Box>
    );
}

export default function MapVoteDialog({
    matchId,
    candidates = [],
    blockedMaps = [],
    expiresAt = null,
    durationSeconds = MAP_VOTE_DEFAULT_DURATION_SECONDS,
    voters = [],
    votes = {},
    myVote = null,
    resolvedMap = null,
    wasTie = false,
    tieBrokenBy = null,
    topCount = null,
    teamStats = null,
    currentUserId,
    onVote,
    onDismiss,
}) {
    const isMobile = useMediaQuery(MOBILE_NAV_MEDIA);
    const [handoffReady, setHandoffReady] = useState(false);

    useEffect(() => {
        setHandoffReady(false);
        const reduceMotion =
            typeof window !== "undefined" &&
            window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        const delay = reduceMotion ? 80 : MAP_VOTE_HANDOFF_MS;
        const id = window.setTimeout(() => setHandoffReady(true), delay);
        return () => window.clearTimeout(id);
    }, [matchId]);

    // Anchors a local deadline when the server omits expiresAt, so the counter
    // still ticks instead of freezing on the full duration forever.
    const localDeadlineRef = useRef(null);
    if (localDeadlineRef.current?.matchId !== matchId) {
        const seconds = Number(durationSeconds);
        localDeadlineRef.current = {
            matchId,
            deadline:
                Date.now() +
                (Number.isFinite(seconds)
                    ? seconds
                    : MAP_VOTE_DEFAULT_DURATION_SECONDS) *
                    1000,
        };
    }
    const deadline =
        expiresAt != null && Number.isFinite(Number(expiresAt))
            ? Number(expiresAt)
            : localDeadlineRef.current.deadline;

    const [timeLeft, setTimeLeft] = useState(() =>
        remainingSeconds(deadline, durationSeconds)
    );

    const safeCandidates = useMemo(
        () =>
            Array.isArray(candidates)
                ? candidates.filter(
                      (mapId) => typeof mapId === "string" && mapId.trim()
                  )
                : [],
        [candidates]
    );

    const safeBlockedMaps = useMemo(
        () =>
            Array.isArray(blockedMaps)
                ? blockedMaps.filter(
                      (blocked) =>
                          blocked &&
                          typeof blocked.mapId === "string" &&
                          blocked.mapId.trim()
                  )
                : [],
        [blockedMaps]
    );

    const safeVoters = useMemo(
        () =>
            Array.isArray(voters)
                ? voters.filter((voter) => voter && voter.userId != null)
                : [],
        [voters]
    );

    const safeVotes = useMemo(() => {
        if (votes && typeof votes === "object" && !Array.isArray(votes)) {
            return votes;
        }
        return {};
    }, [votes]);

    const viewerId =
        currentUserId != null && currentUserId !== ""
            ? String(currentUserId)
            : null;

    const selectedMapId = useMemo(() => {
        const serverPick = viewerId
            ? (safeVotes[viewerId] ?? safeVotes[currentUserId] ?? null)
            : null;
        const localPick = typeof myVote === "string" && myVote ? myVote : null;

        // While voting, the optimistic local pick leads so the tile responds
        // instantly. Once the vote is closed the server tally is final, so an
        // optimistic pick that never reached the server must not outlive it.
        const pick = resolvedMap ? serverPick : (localPick ?? serverPick);

        if (typeof pick !== "string" || !safeCandidates.includes(pick)) {
            return null;
        }
        return pick;
    }, [myVote, viewerId, currentUserId, safeVotes, safeCandidates, resolvedMap]);

    const votesByMap = useMemo(() => {
        const grouped = Object.fromEntries(
            safeCandidates.map((mapId) => [mapId, []])
        );

        // Iterate the roster (not Object.keys(votes)) so a changed pick moves with the voter and unknown userIds are dropped.
        for (const voter of safeVoters) {
            const userId = voter.userId;
            const isSelf = viewerId != null && String(userId) === viewerId;
            const picked = isSelf
                ? selectedMapId
                : safeVotes[userId] ?? safeVotes[String(userId)];
            if (typeof picked !== "string" || !grouped[picked]) continue;
            grouped[picked].push(voter);
        }

        return grouped;
    }, [safeCandidates, safeVoters, safeVotes, selectedMapId, viewerId]);

    const pendingVoters = useMemo(() => {
        const votedIds = new Set(
            Object.values(votesByMap).flatMap((list) =>
                list.map((voter) => String(voter.userId))
            )
        );
        return safeVoters.filter(
            (voter) => !votedIds.has(String(voter.userId))
        );
    }, [safeVoters, votesByMap]);

    const voteLocked = Boolean(resolvedMap) || timeLeft <= 0;
    const isUrgent =
        timeLeft > 0 &&
        timeLeft <=
            Math.ceil(
                (Number(durationSeconds) || MAP_VOTE_DEFAULT_DURATION_SECONDS) /
                    3
            );
    const winnerId =
        typeof resolvedMap === "string" && resolvedMap ? resolvedMap : null;
    const winnerMeta = winnerId ? getMapMetaById(winnerId) : null;
    const candidateCount = safeCandidates.length;
    const columnCount = isMobile
        ? Math.min(2, Math.max(candidateCount, 1))
        : Math.min(3, Math.max(candidateCount, 1));
    const duration = Number(durationSeconds);
    const progressPct =
        Number.isFinite(duration) && duration > 0
            ? Math.min(100, (timeLeft / duration) * 100)
            : 0;

    useEffect(() => {
        const tick = () => {
            setTimeLeft(remainingSeconds(deadline, durationSeconds));
        };

        tick();
        const id = window.setInterval(tick, 1000);
        return () => window.clearInterval(id);
    }, [deadline, durationSeconds]);

    // Held in a ref so the timer below never restarts just because the parent
    // handed down a new callback identity — a reset loop would silently
    // disable the only escape from a blocking overlay.
    const onDismissRef = useRef(onDismiss);
    useEffect(() => {
        onDismissRef.current = onDismiss;
    }, [onDismiss]);

    // Escape hatch. Nothing else can dismiss this overlay, so if the winner is
    // shown (or the server goes quiet past the deadline) the modal must release
    // the UI on its own rather than locking the user out of the whole app.
    useEffect(() => {
        const delay = resolvedMap
            ? WINNER_DISPLAY_MS
            : Math.max(0, deadline - Date.now()) + RESOLVE_GRACE_MS;

        const id = window.setTimeout(() => {
            if (typeof onDismissRef.current === "function") {
                onDismissRef.current();
            }
        }, delay);

        return () => window.clearTimeout(id);
    }, [resolvedMap, deadline]);

    useEffect(() => {
        const edgeBasePath = utils.getEdgeBasePath();
        const images = safeCandidates
            .map((mapId) => getMapImageUrl(mapId, edgeBasePath))
            .filter(Boolean)
            .map((url) => {
                const img = new Image();
                img.src = url;
                return img;
            });

        return () => {
            images.forEach((img) => {
                img.src = "";
            });
        };
    }, [safeCandidates]);

    const handleVote = (mapId) => {
        if (voteLocked) return;
        if (typeof onVote !== "function") return;
        onVote(mapId);
    };

    if (!handoffReady) return null;

    // BackdropOverlay dismisses when onClick is set; omitting it makes the vote unskippable.
    return (
        <BackdropOverlay
            open
            zIndex={1400}
            sx={{
                px: { xs: 1.25, sm: 2 },
                alignItems: { xs: "flex-start", md: "center" },
                pt: {
                    xs: "calc(0.65rem + env(safe-area-inset-top, 0px))",
                    md: 0,
                },
                pb: {
                    xs: `calc(${MOBILE_NAV_HEIGHT} + 0.65rem + env(safe-area-inset-bottom, 0px))`,
                    md: 0,
                },
                overflowY: "auto",
            }}
            contentSx={{
                width: "100%",
                maxWidth: {
                    xs: "100%",
                    md:
                        candidateCount <= 3
                            ? "42rem"
                            : candidateCount <= 6
                              ? "54rem"
                              : "64rem",
                },
            }}
        >
            {/* The overlay blocks the page but is not a real MUI Dialog, so
                without a trap keyboard users tab straight behind it. */}
            <TrapFocus open disableAutoFocus={false} disableEnforceFocus={false}>
            <MotionBox
                tabIndex={-1}
                role="dialog"
                aria-modal="true"
                aria-labelledby="map-vote-dialog-title"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.15, ease: "easeInOut" }}
                sx={{
                    position: "relative",
                    width: "100%",
                    borderRadius: { xs: "18px", md: "26px" },
                    overflow: "hidden",
                    background: `
                        linear-gradient(
                            180deg,
                            rgba(18,18,22,0.985) 0%,
                            rgba(11,11,15,0.992) 58%,
                            rgba(7,7,10,1) 100%
                        )
                    `,
                    border: `1px solid ${colors.border.default}`,
                    backdropFilter: "blur(22px)",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        pointerEvents: "none",
                        background: `
                            radial-gradient(
                                circle at top,
                                rgba(232,163,61,0.13),
                                transparent 56%
                            )
                        `,
                    },
                }}
            >
                <Box
                    sx={{
                        width: "100%",
                        height: "3px",
                        backgroundColor: "rgba(255,255,255,0.04)",
                        overflow: "hidden",
                    }}
                >
                    <Box
                        sx={{
                            height: "100%",
                            width: `${progressPct}%`,
                            transition: "width 0.15s ease-in-out",
                            background: isUrgent
                                ? colors.state.danger.main
                                : colors.primary.main,
                        }}
                    />
                </Box>

                <Box
                    sx={{
                        position: "relative",
                        zIndex: 1,
                        p: {
                            xs: "1.15rem",
                            sm: "1.25rem",
                            md: "1.35rem",
                        },
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: { xs: "0.7rem", md: "0.8rem" },
                            mb: { xs: "1rem", md: "0.95rem" },
                            minWidth: 0,
                        }}
                    >
                        <Box
                            sx={{
                                width: { xs: "2.85rem", md: "3.3rem" },
                                height: { xs: "2.85rem", md: "3.3rem" },
                                borderRadius: "50%",
                                flexShrink: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: `
                                    radial-gradient(
                                        circle at top,
                                        rgba(232,163,61,0.28),
                                        rgba(232,163,61,0.05)
                                    )
                                `,
                                border: "1px solid rgba(232,163,61,0.18)",
                            }}
                        >
                            <HowToVote
                                sx={{
                                    fontSize: { xs: "1.35rem", md: "1.55rem" },
                                    color: colors.accent.main,
                                }}
                            />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                id="map-vote-dialog-title"
                                sx={{
                                    fontSize: { xs: "1.2rem", md: "1.35rem" },
                                    fontWeight: 600,
                                    color: colors.text.primary,
                                    lineHeight: 1,
                                }}
                            >
                                Map Vote
                            </Typography>
                            <Typography
                                sx={{
                                    mt: "0.28rem",
                                    color: colors.text.secondary,
                                    fontSize: { xs: "0.72rem", md: "0.76rem" },
                                    letterSpacing: "0.04em",
                                }}
                            >
                                {winnerId
                                    ? "Vote closed"
                                    : `Pick a map · last ${
                                          Number(teamStats?.windowDays) > 0
                                              ? Number(teamStats.windowDays)
                                              : 90
                                      }d team records`}
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        aria-live="polite"
                        aria-atomic="true"
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: { xs: "0.95rem", md: "1.05rem" },
                            py: { xs: "0.55rem", md: "0.7rem" },
                            borderRadius: { xs: "12px", md: "14px" },
                            border: isUrgent
                                ? "1px solid rgba(220,38,38,0.24)"
                                : `1px solid ${colors.border.default}`,
                            backgroundColor: isUrgent
                                ? "rgba(220,38,38,0.10)"
                                : "rgba(232,163,61,0.08)",
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: { xs: "0.62rem", md: "0.64rem" },
                                fontWeight: 700,
                                letterSpacing: "0.14em",
                                textTransform: "uppercase",
                                color: colors.text.muted,
                                mb: "0.15rem",
                            }}
                        >
                            Time remaining
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: isMobile ? "1.85rem" : "2.25rem",
                                fontWeight: 700,
                                lineHeight: 1,
                                fontVariantNumeric: "tabular-nums",
                                color: isUrgent
                                    ? colors.state.danger.main
                                    : colors.primary.main,
                            }}
                        >
                            {timeLeft}s
                        </Typography>
                    </Box>

                    <MapCooldownNotice blockedMaps={safeBlockedMaps} />

                    {candidateCount === 0 ? (
                        <Typography
                            sx={{
                                textAlign: "center",
                                color: colors.text.secondary,
                                fontSize: "0.88rem",
                                py: "1.5rem",
                            }}
                        >
                            Waiting for map options
                        </Typography>
                    ) : (
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
                                gap: { xs: "0.7rem", md: "0.85rem" },
                            }}
                        >
                            {safeCandidates.map((mapId) => {
                                const meta = getMapMetaById(mapId);
                                const display = meta?.display || mapId;
                                const imageUrl = getMapImageUrl(
                                    mapId,
                                    utils.getEdgeBasePath()
                                );
                                const mapVoters = votesByMap[mapId] || [];
                                const isSelected = selectedMapId === mapId;
                                const isWinner = winnerId === mapId;

                                return (
                                    <Box
                                        key={mapId}
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            minWidth: 0,
                                        }}
                                    >
                                        <ButtonBase
                                            disabled={voteLocked}
                                            onClick={() => handleVote(mapId)}
                                            aria-pressed={isSelected}
                                            aria-label={`Vote for ${display}`}
                                            sx={{
                                                display: "block",
                                                width: "100%",
                                                textAlign: "left",
                                                borderRadius: {
                                                    xs: "12px",
                                                    md: "14px",
                                                },
                                                overflow: "hidden",
                                                border: isWinner
                                                    ? `1px solid ${colors.primary.main}`
                                                    : isSelected
                                                      ? `1px solid ${colors.primary.main}`
                                                      : `1px solid ${colors.border.default}`,
                                                backgroundColor: isSelected
                                                    ? colors.accent.subtle
                                                    : colors.background.surface,
                                                transition:
                                                    "border-color 0.15s ease-in-out, background-color 0.15s ease-in-out",
                                                "&:hover": voteLocked
                                                    ? undefined
                                                    : {
                                                          borderColor:
                                                              colors.primary
                                                                  .main,
                                                          backgroundColor:
                                                              colors.accent
                                                                  .subtle,
                                                      },
                                                "&.Mui-disabled": {
                                                    opacity: isWinner ? 1 : 0.92,
                                                },
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    position: "relative",
                                                    aspectRatio: "16 / 10",
                                                    minHeight: {
                                                        xs: "5.25rem",
                                                        md: "6.25rem",
                                                    },
                                                    backgroundColor:
                                                        colors.background.card,
                                                    backgroundImage: imageUrl
                                                        ? `url(${imageUrl})`
                                                        : "none",
                                                    backgroundSize: "cover",
                                                    backgroundPosition:
                                                        "center",
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        inset: 0,
                                                        background: `
                                                            linear-gradient(
                                                                180deg,
                                                                rgba(0,0,0,0.08) 0%,
                                                                rgba(0,0,0,0.52) 62%,
                                                                rgba(0,0,0,0.88) 100%
                                                            )
                                                        `,
                                                    }}
                                                />
                                                {isWinner && (
                                                    <Box
                                                        sx={{
                                                            position:
                                                                "absolute",
                                                            top: "0.45rem",
                                                            left: "0.45rem",
                                                            zIndex: 1,
                                                            px: "0.45rem",
                                                            py: "0.12rem",
                                                            borderRadius: "8px",
                                                            border: `1px solid ${alpha(
                                                                colors.primary
                                                                    .main,
                                                                0.5
                                                            )}`,
                                                            backgroundColor:
                                                                alpha(
                                                                    colors
                                                                        .background
                                                                        .dark,
                                                                    0.78
                                                                ),
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontSize:
                                                                    "0.58rem",
                                                                fontWeight: 800,
                                                                letterSpacing:
                                                                    "0.12em",
                                                                textTransform:
                                                                    "uppercase",
                                                                color: colors
                                                                    .primary
                                                                    .main,
                                                            }}
                                                        >
                                                            Winner
                                                        </Typography>
                                                    </Box>
                                                )}
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        left: 0,
                                                        right: 0,
                                                        bottom: 0,
                                                        p: "0.55rem 0.65rem",
                                                        display: "flex",
                                                        alignItems:
                                                            "flex-end",
                                                        justifyContent:
                                                            "space-between",
                                                        gap: "0.4rem",
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            color: colors.text
                                                                .primary,
                                                            fontSize: {
                                                                xs: "0.88rem",
                                                                md: "0.95rem",
                                                            },
                                                            fontWeight: 600,
                                                            lineHeight: 1.15,
                                                            textShadow:
                                                                "0 4px 12px rgba(0,0,0,0.65)",
                                                        }}
                                                    >
                                                        {display}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            flexShrink: 0,
                                                            fontSize: "0.68rem",
                                                            fontWeight: 700,
                                                            color: colors
                                                                .accent.main,
                                                            fontVariantNumeric:
                                                                "tabular-nums",
                                                        }}
                                                    >
                                                        {mapVoters.length}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </ButtonBase>

                                        <MapTeamRecords
                                            mapId={mapId}
                                            teamStats={teamStats}
                                        />

                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: "0.28rem",
                                                mt: "0.4rem",
                                                minHeight: "1.55rem",
                                            }}
                                        >
                                            {mapVoters.map((voter) => (
                                                <MapVoterChip
                                                    key={String(voter.userId)}
                                                    voter={voter}
                                                    isSelf={
                                                        viewerId != null &&
                                                        String(voter.userId) ===
                                                            viewerId
                                                    }
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}

                    <Box
                        sx={{
                            mt: { xs: "1rem", md: "1.05rem" },
                            minHeight: "1.2rem",
                            textAlign: "center",
                        }}
                    >
                        <Typography
                            sx={{
                                color: winnerId
                                    ? colors.primary.main
                                    : colors.text.secondary,
                                fontSize: { xs: "0.84rem", md: "0.9rem" },
                                fontWeight: winnerId ? 700 : 600,
                                letterSpacing: "0.02em",
                            }}
                        >
                            {winnerId
                                ? resolvedVoteLabel({
                                      winnerId,
                                      winnerMeta,
                                      wasTie,
                                      tieBrokenBy,
                                      topCount,
                                  })
                                : waitingLabel(
                                      pendingVoters.length,
                                      safeVoters.length
                                  )}
                        </Typography>
                    </Box>
                </Box>
            </MotionBox>
            </TrapFocus>
        </BackdropOverlay>
    );
}
